/**
 * Navigation — Sidebar-Menü, Akten-Prozessmenü (pro Betrieb), Einstellungen
 * Mit Funktionsstatus-Labels für jede Funktion
 *
 * WICHTIG: Jeder Betrieb hat eigene Prozessschritte!
 *
 * MENÜSTRUKTUR v8.0 (31.03.2026):
 * - MITARBEITER sehen NUR: Kommunikation, Aufgaben, Kalender, Einstellungen
 * - ADMIN sieht alles (Dashboard, Akten, Kontakte, CRM, Marketing, Finanzen etc.)
 * - Andere Module werden nach und nach für Mitarbeiter freigeschaltet
 * - requiredRoles: ['admin'] = nur Admin sieht das Modul
 *
 * v8.1 (Feature buchhaltung-ein-ort): Finanzen-Untermenü konsolidiert.
 *
 * v9.0 (Feature finanzen-cockpit): Finanzen ist ein Mandanten-Cockpit.
 * v9.1 (Feature bwa-auswertungen): Sektion „BWA“ je Firma.
 * v9.2 (Feature finanz-aufgaben): Sektion „Aufgaben“ je Firma.
 * v9.3 (Feature steuer-cockpit): Neuer Mandant „Steuer/ESt“ im Finanzen-Cockpit
 *   (Veranlagung, Anlage V, § 23-Verkäufe, Fahrtkosten, Dokumente).
 * v9.4 (Feature finanz-pruefung): Sektion „Prüfung“ je Firma
 *   (Vollständigkeitskontrolle + Buchungsjournal für den Steuerberater).
 * v9.5 (Feature finanz-steuerberater): Sektion „Steuerberater“ je Firma
 *   (Kanzlei-Historie, Kosten alt vs. aktuell, Regress-Dossier).
 * v9.6 (Feature ust-organschaft): Sektion „USt-Organschaft“ nur unter „Alle“
 *   (konsolidierte USt der Organschaft SV-Büro + LCS GmbH, Entwurf).
 * v9.7 (Feature forderungs-cockpit): Neuer Top-Level-Menüpunkt „Forderungs-Cockpit“
 *   (morgendliche Fall-Prüfungs-Ansicht des Forderungsmanagers, /forderungs-cockpit).
 * v9.8 (Feature mailbox-reconnect): Neuer Menüpunkt „E-Mail verbinden“
 *   (geteilte Gmail-Postfächer (neu-)verbinden, /mail-verbinden, admin).
 */

import type { FeatureStatus, BetriebId } from './betriebe';
import type { UserRole, PermissionLevel } from '@/contexts/AuthContext';

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  status: FeatureStatus;
  badge?: number;
  children?: NavChild[];
  /** Mindest-Permission-Level (0=alle, 1=lesen, 2=erstellen, 3=bearbeiten, 4=vollzugriff) */
  requiredPermission?: { module: string; level: PermissionLevel };
  /** Nur für bestimmte Rollen sichtbar */
  requiredRoles?: UserRole[];
  /** Trennlinie vor diesem Item anzeigen */
  separator?: boolean;
}

export interface NavChild {
  id: string;
  label: string;
  betriebId: string;
  color: string;
  status: FeatureStatus;
  path?: string; // Optional override path for non-betrieb children
}

export interface AktenProzessItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  status: FeatureStatus;
  processStatus?: 'offen' | 'in_bearbeitung' | 'fertig';
}

// ============================================================
// HAUPT-SIDEBAR-NAVIGATION v8.0
// ============================================================
export const SIDEBAR_NAV: NavItem[] = [
  { id: 'kommunikation', label: 'Kommunikation', icon: 'Mail', path: '/kommunikation', status: 'aktiv' },
  { id: 'aufgaben', label: 'Aufgaben', icon: 'CheckSquare', path: '/aufgaben', status: 'aktiv' },
  { id: 'kalender', label: 'Kalender', icon: 'Calendar', path: '/kalender', status: 'aktiv' },
  { id: 'stellungnahmen', label: 'Stellungnahmen', icon: 'Scale', path: '/stellungnahmen', status: 'aktiv', requiredRoles: ['admin'] },
  {
    id: 'akten',
    label: 'Akten',
    icon: 'FolderOpen',
    path: '/akten',
    status: 'aktiv',
    separator: true,
    requiredRoles: ['admin'],
    children: [
      { id: 'gutachten', label: 'Kfz Gutachten', betriebId: 'gutachten', color: '#2563EB', status: 'aktiv' },
      { id: 'autoglas', label: 'Autoglas', betriebId: 'autoglas', color: '#059669', status: 'aktiv' },
      { id: 'mietwagen', label: 'Mietwagen', betriebId: 'mietwagen', color: '#D97706', status: 'aktiv' },
      { id: 'werkstatt', label: 'Werkstatt', betriebId: 'werkstatt', color: '#DC2626', status: 'aktiv' },
      { id: 'protex', label: 'Protex / Amazon', betriebId: 'protex', color: '#7C3AED', status: 'geplant' },
      { id: 'unfallgiganten', label: 'Unfallgiganten', betriebId: 'unfallgiganten', color: '#0D9488', status: 'geplant' },
      { id: 'airbnb', label: 'Airbnb / Immobilien', betriebId: 'airbnb', color: '#B45309', status: 'geplant' },
      { id: 'recht', label: 'Recht & Forderungen', betriebId: 'recht', color: '#1E3A5F', status: 'geplant' },
    ],
  },
  { id: 'kontakte', label: 'Kontakte', icon: 'Users', path: '/kontakte', status: 'aktiv', requiredRoles: ['admin'] },
  {
    id: 'werkstatt',
    label: 'Werkstatt',
    icon: 'Wrench',
    path: '/werkstatt/angebote',
    status: 'aktiv',
    separator: true,
    requiredRoles: ['admin'],
    children: [
      { id: 'werkstatt-angebote', label: 'Angebote', betriebId: 'werkstatt', color: '#DC2626', status: 'aktiv' as FeatureStatus, path: '/werkstatt/angebote' },
      { id: 'werkstatt-kunden', label: 'Kunden', betriebId: 'werkstatt', color: '#D97706', status: 'aktiv' as FeatureStatus, path: '/werkstatt/kunden' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: 'Megaphone',
    path: '/marketing',
    status: 'aktiv',
    requiredRoles: ['admin'],
    children: [
      { id: 'marketing-uebersicht', label: 'Übersicht', betriebId: 'marketing', color: '#7C3AED', status: 'aktiv' as FeatureStatus, path: '/marketing' },
      { id: 'marketing-posts', label: 'Post-Generator', betriebId: 'marketing', color: '#DB2777', status: 'aktiv' as FeatureStatus, path: '/marketing/posts' },
      { id: 'marketing-pipeline', label: 'Content-Pipeline', betriebId: 'marketing', color: '#059669', status: 'aktiv' as FeatureStatus, path: '/marketing/pipeline' },
      { id: 'marketing-statistik', label: 'Statistik', betriebId: 'marketing', color: '#2563EB', status: 'aktiv' as FeatureStatus, path: '/marketing/statistik' },
    ],
  },
  { id: 'informationsschreiben', label: 'Informationsschreiben', icon: 'FileSignature', path: '/informationsschreiben', status: 'aktiv', requiredRoles: ['admin'] },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'aktiv', requiredRoles: ['admin'] },
  { id: 'forderungs-cockpit', label: 'Forderungs-Cockpit', icon: 'Inbox', path: '/forderungs-cockpit', status: 'aktiv', separator: true, requiredRoles: ['admin'] },
  { id: 'finanzen', label: 'Finanzen', icon: 'Wallet', path: '/finanzen', status: 'aktiv', separator: true, requiredRoles: ['admin'] },
  { id: 'mail-verbinden', label: 'E-Mail verbinden', icon: 'Link', path: '/mail-verbinden', status: 'aktiv', separator: true, requiredRoles: ['admin'] },
  { id: 'einstellungen', label: 'Einstellungen', icon: 'Settings', path: '/einstellungen', status: 'aktiv', separator: true },
];

// ============================================================
// INDIVIDUELLE PROZESSSCHRITTE PRO BETRIEB
// ============================================================

const PROZESS_GUTACHTEN: AktenProzessItem[] = [
  { id: 'uebersicht', label: 'Akte', icon: 'FolderOpen', path: '/uebersicht', status: 'aktiv' },
  { id: 'auftrag', label: 'Auftrag', icon: 'ClipboardList', path: '/auftrag', status: 'aktiv' },
  { id: 'beteiligte', label: 'Beteiligte', icon: 'Users', path: '/beteiligte', status: 'aktiv' },
  { id: 'unterschrift', label: 'Unterschrift', icon: 'PenTool', path: '/unterschrift', status: 'geplant' },
  { id: 'fahrzeug', label: 'Fahrzeug', icon: 'Car', path: '/fahrzeug', status: 'aktiv' },
  { id: 'besichtigungen', label: 'Besichtigungen', icon: 'Eye', path: '/besichtigungen', status: 'aktiv' },
  { id: 'begutachtung', label: 'Begutachtung', icon: 'Search', path: '/begutachtung', status: 'geplant' },
  { id: 'lichtbilder', label: 'Lichtbilder', icon: 'Camera', path: '/lichtbilder', status: 'aktiv' },
  { id: 'instandsetzung', label: 'Instandsetzung', icon: 'Wrench', path: '/instandsetzung', status: 'geplant' },
  { id: 'bewertung', label: 'Bewertung', icon: 'BarChart3', path: '/bewertung', status: 'aktiv' },
  { id: 'restwert', label: 'Restwert', icon: 'TrendingDown', path: '/restwert', status: 'aktiv' },
  { id: 'minderwert', label: 'Minderwert', icon: 'ArrowDownRight', path: '/minderwert', status: 'aktiv' },
  { id: 'beurteilung', label: 'Beurteilung', icon: 'FileCheck', path: '/beurteilung', status: 'geplant' },
  { id: 'gutachten', label: 'Gutachten', icon: 'FileText', path: '/gutachten', status: 'aktiv' },
  { id: 'stellungnahme', label: 'Stellungnahme', icon: 'Scale', path: '/stellungnahme', status: 'aktiv' },
  { id: 'rechnung', label: 'Rechnung', icon: 'Receipt', path: '/rechnung', status: 'aktiv' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'aktiv' },
  { id: 'protokoll', label: 'Protokoll', icon: 'ScrollText', path: '/protokoll', status: 'aktiv' },
];

const PROZESS_GUTACHTEN_SV: AktenProzessItem[] = [
  { id: 'uebersicht', label: 'Akte', icon: 'FolderOpen', path: '/uebersicht', status: 'aktiv' },
  { id: 'auftrag', label: 'Verfahren', icon: 'Scale', path: '/auftrag', status: 'aktiv' },
  { id: 'beteiligte', label: 'Ausschuss & Beteiligte', icon: 'Users', path: '/beteiligte', status: 'aktiv' },
  { id: 'fahrzeug', label: 'Fahrzeug', icon: 'Car', path: '/fahrzeug', status: 'aktiv' },
  { id: 'besichtigungen', label: 'Sitzung / Besichtigung', icon: 'Eye', path: '/besichtigungen', status: 'aktiv' },
  { id: 'gutachten', label: 'Stellungnahme', icon: 'FileCheck', path: '/gutachten', status: 'aktiv' },
  { id: 'protokoll', label: 'Protokoll', icon: 'ScrollText', path: '/protokoll', status: 'aktiv' },
  { id: 'rechnung', label: 'Rechnung', icon: 'Receipt', path: '/rechnung', status: 'aktiv' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'aktiv' },
];

const PROZESS_AUTOGLAS: AktenProzessItem[] = [
  { id: 'auftrag', label: 'Auftrag', icon: 'ClipboardList', path: '/auftrag', status: 'aktiv' },
  { id: 'fahrzeug', label: 'Fahrzeug', icon: 'Car', path: '/fahrzeug', status: 'aktiv' },
  { id: 'kundendaten', label: 'Kundendaten', icon: 'Users', path: '/kundendaten', status: 'aktiv' },
  { id: 'scheibe', label: 'Scheibe & Material', icon: 'Layers', path: '/scheibe', status: 'geplant' },
  { id: 'terminplanung', label: 'Terminplanung', icon: 'Calendar', path: '/terminplanung', status: 'geplant' },
  { id: 'montage', label: 'Montage', icon: 'Wrench', path: '/montage', status: 'geplant' },
  { id: 'qualitaet', label: 'Qualitätskontrolle', icon: 'CheckCircle', path: '/qualitaet', status: 'geplant' },
  { id: 'rechnung', label: 'Rechnung', icon: 'Receipt', path: '/rechnung', status: 'geplant' },
  { id: 'garantie', label: 'Garantie', icon: 'Shield', path: '/garantie', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

const PROZESS_MIETWAGEN: AktenProzessItem[] = [
  { id: 'anfrage', label: 'Anfrage', icon: 'MessageSquare', path: '/anfrage', status: 'aktiv' },
  { id: 'verfuegbarkeit', label: 'Verfügbarkeit', icon: 'Search', path: '/verfuegbarkeit', status: 'aktiv' },
  { id: 'mietvertrag', label: 'Mietvertrag', icon: 'FileText', path: '/mietvertrag', status: 'aktiv' },
  { id: 'uebergabe', label: 'Fahrzeugübergabe', icon: 'ArrowRightLeft', path: '/uebergabe', status: 'aktiv' },
  { id: 'laufzeit', label: 'Laufzeit & Verlängerung', icon: 'Clock', path: '/laufzeit', status: 'aktiv' },
  { id: 'rueckgabe', label: 'Rückgabe', icon: 'ArrowLeftRight', path: '/rueckgabe', status: 'aktiv' },
  { id: 'schadenscheck', label: 'Schadenscheck', icon: 'AlertTriangle', path: '/schadenscheck', status: 'aktiv' },
  { id: 'abrechnung', label: 'Abrechnung', icon: 'Receipt', path: '/abrechnung', status: 'aktiv' },
  { id: 'fahrzeuge', label: 'Fahrzeuge', icon: 'Car', path: '/fahrzeuge', status: 'aktiv' },
  { id: 'forderungen', label: 'Forderungen', icon: 'Scale', path: '/forderungen', status: 'aktiv' },
  { id: 'korrespondenz', label: 'Korrespondenz', icon: 'Mail', path: '/korrespondenz', status: 'aktiv' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'aktiv' },
];

const PROZESS_WERKSTATT: AktenProzessItem[] = [
  { id: 'auftrag', label: 'Auftrag', icon: 'ClipboardList', path: '/auftrag', status: 'aktiv' },
  { id: 'fahrzeugeingang', label: 'Fahrzeugeingang', icon: 'LogIn', path: '/fahrzeugeingang', status: 'geplant' },
  { id: 'diagnose', label: 'Diagnose', icon: 'Stethoscope', path: '/diagnose', status: 'geplant' },
  { id: 'kostenvoranschlag', label: 'Kostenvoranschlag', icon: 'Calculator', path: '/kostenvoranschlag', status: 'geplant' },
  { id: 'freigabe', label: 'Freigabe', icon: 'CheckCircle', path: '/freigabe', status: 'geplant' },
  { id: 'ausfuehrung', label: 'Arbeitsausführung', icon: 'Wrench', path: '/ausfuehrung', status: 'geplant' },
  { id: 'teile', label: 'Teilebestellung', icon: 'Package', path: '/teile', status: 'geplant' },
  { id: 'qualitaet', label: 'Qualitätskontrolle', icon: 'Shield', path: '/qualitaet', status: 'geplant' },
  { id: 'fahrzeugausgabe', label: 'Fahrzeugausgabe', icon: 'LogOut', path: '/fahrzeugausgabe', status: 'geplant' },
  { id: 'rechnung', label: 'Rechnung', icon: 'Receipt', path: '/rechnung', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

const PROZESS_PROTEX: AktenProzessItem[] = [
  { id: 'bestellung', label: 'Bestellung', icon: 'ShoppingCart', path: '/bestellung', status: 'geplant' },
  { id: 'lager', label: 'Lager & Bestand', icon: 'Package', path: '/lager', status: 'geplant' },
  { id: 'versand', label: 'Versand', icon: 'Truck', path: '/versand', status: 'geplant' },
  { id: 'tracking', label: 'Tracking', icon: 'MapPin', path: '/tracking', status: 'geplant' },
  { id: 'retoure', label: 'Retoure', icon: 'RotateCcw', path: '/retoure', status: 'geplant' },
  { id: 'abrechnung', label: 'Abrechnung', icon: 'Receipt', path: '/abrechnung', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

const PROZESS_UNFALLGIGANTEN: AktenProzessItem[] = [
  { id: 'lead', label: 'Lead', icon: 'UserPlus', path: '/lead', status: 'geplant' },
  { id: 'kontaktaufnahme', label: 'Kontaktaufnahme', icon: 'Phone', path: '/kontaktaufnahme', status: 'geplant' },
  { id: 'beratung', label: 'Beratung', icon: 'MessageSquare', path: '/beratung', status: 'geplant' },
  { id: 'auftrag', label: 'Auftrag', icon: 'ClipboardList', path: '/auftrag', status: 'geplant' },
  { id: 'abwicklung', label: 'Abwicklung', icon: 'Settings', path: '/abwicklung', status: 'geplant' },
  { id: 'abschluss', label: 'Abschluss', icon: 'CheckCircle', path: '/abschluss', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

const PROZESS_RECHT: AktenProzessItem[] = [
  { id: 'akte', label: 'Rechtsakte', icon: 'Scale', path: '/akte', status: 'geplant' },
  { id: 'forderung', label: 'Forderung', icon: 'FileText', path: '/forderung', status: 'geplant' },
  { id: 'beteiligte', label: 'Beteiligte', icon: 'Users', path: '/beteiligte', status: 'geplant' },
  { id: 'schriftverkehr', label: 'Schriftverkehr', icon: 'Mail', path: '/schriftverkehr', status: 'geplant' },
  { id: 'fristen', label: 'Fristen', icon: 'Clock', path: '/fristen', status: 'geplant' },
  { id: 'klage', label: 'Klage', icon: 'Gavel', path: '/klage', status: 'geplant' },
  { id: 'vergleich', label: 'Vergleich', icon: 'Handshake', path: '/vergleich', status: 'geplant' },
  { id: 'abschluss', label: 'Abschluss', icon: 'CheckCircle', path: '/abschluss', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

const PROZESS_AIRBNB: AktenProzessItem[] = [
  { id: 'anfrage', label: 'Anfrage', icon: 'MessageSquare', path: '/anfrage', status: 'geplant' },
  { id: 'buchung', label: 'Buchungsbestätigung', icon: 'CalendarCheck', path: '/buchung', status: 'geplant' },
  { id: 'checkin', label: 'Check-in', icon: 'LogIn', path: '/checkin', status: 'geplant' },
  { id: 'laufzeit', label: 'Laufzeit', icon: 'Clock', path: '/laufzeit', status: 'geplant' },
  { id: 'checkout', label: 'Check-out', icon: 'LogOut', path: '/checkout', status: 'geplant' },
  { id: 'reinigung', label: 'Reinigung & Übergabe', icon: 'Sparkles', path: '/reinigung', status: 'geplant' },
  { id: 'abrechnung', label: 'Abrechnung', icon: 'Receipt', path: '/abrechnung', status: 'geplant' },
  { id: 'dokumente', label: 'Dokumente', icon: 'Files', path: '/dokumente', status: 'geplant' },
];

export const BETRIEB_PROZESS_NAV: Record<BetriebId, AktenProzessItem[]> = {
  gutachten: PROZESS_GUTACHTEN,
  autoglas: PROZESS_AUTOGLAS,
  mietwagen: PROZESS_MIETWAGEN,
  werkstatt: PROZESS_WERKSTATT,
  protex: PROZESS_PROTEX,
  unfallgiganten: PROZESS_UNFALLGIGANTEN,
  airbnb: PROZESS_AIRBNB,
  recht: PROZESS_RECHT,
  unfallwiki: PROZESS_UNFALLGIGANTEN,
  app_programmierung: [],
};

// ============================================================
// FINANZEN-COCKPIT — Firmen (Mandanten) + Firma-Untermenü (v9.0)
// ============================================================

/** Firma-IDs im Finanzen-Cockpit (Mandanten-Umschalter). */
export type FinanzenFirmaId = 'sv_buero' | 'lcs' | 'privat' | 'immobilien' | 'steuer' | 'alle';

/** Eine Firma (Mandant) im Finanzen-Cockpit. */
export interface FinanzenFirma {
  id: FinanzenFirmaId;
  label: string;
  betriebKey?: 'sv' | 'lcs' | 'alle';
  farbe: string;
  icon: string;
}

/** Eine Sektion im Firma-Untermenü (analog AktenProzessItem). */
export interface FinanzSektion {
  id: string;
  label: string;
  icon: string;
  path?: string;
  status?: FeatureStatus;
}

/** Firmen-Liste (Mandanten-Umschalter, in Reihenfolge der Anzeige). */
export const FINANZEN_FIRMEN: FinanzenFirma[] = [
  { id: 'sv_buero', label: 'SV Büro', betriebKey: 'sv', farbe: '#2563EB', icon: 'Scale' },
  { id: 'lcs', label: 'LCS', betriebKey: 'lcs', farbe: '#D97706', icon: 'Car' },
  { id: 'privat', label: 'Privat', farbe: '#0F766E', icon: 'Wallet' },
  { id: 'immobilien', label: 'Immobilien', farbe: '#B45309', icon: 'Building2' },
  { id: 'steuer', label: 'Steuer/ESt', farbe: '#7C3AED', icon: 'Calculator' },
  { id: 'alle', label: 'Alle', betriebKey: 'alle', farbe: '#0D9488', icon: 'Layers' },
];

/** SV Büro & LCS — identischer Sektions-Satz (operative Finanz-Bereiche). */
const FINANZ_SEKTIONEN_BETRIEB: FinanzSektion[] = [
  { id: 'uebersicht', label: 'Übersicht', icon: 'BarChart3' },
  { id: 'aufgaben', label: 'Aufgaben', icon: 'ListChecks' },
  { id: 'eingangsrechnungen', label: 'Eingangsrechnungen', icon: 'ArrowDownToLine' },
  { id: 'ausgangsrechnungen', label: 'Ausgangsrechnungen', icon: 'ArrowUpFromLine' },
  { id: 'buchhaltung', label: 'Buchhaltung / StB', icon: 'CalendarRange' },
  { id: 'bwa', label: 'BWA', icon: 'BarChart3' },
  { id: 'pruefung', label: 'Prüfung', icon: 'ClipboardCheck' },
  { id: 'steuerberater', label: 'Steuerberater', icon: 'Scale' },
  { id: 'dokumente', label: 'Dokumente', icon: 'FileText' },
  { id: 'fahrzeuge', label: 'Fahrzeuge', icon: 'Car' },
  { id: 'kontoauszuege', label: 'Bank', icon: 'Receipt' },
  { id: 'ueberweisungen', label: 'Überweisungen', icon: 'CreditCard' },
  { id: 'forderungen', label: 'Forderungen', icon: 'Scale' },
  { id: 'liquiditaet', label: 'Liquidität', icon: 'Wallet' },
  { id: 'datev', label: 'DATEV', icon: 'Download' },
];

/** Immobilien — eigener Sektions-Satz. */
const FINANZ_SEKTIONEN_IMMOBILIEN: FinanzSektion[] = [
  { id: 'uebersicht', label: 'Übersicht', icon: 'BarChart3' },
  { id: 'objekte', label: 'Objekte', icon: 'Building2' },
  { id: 'mietvertraege', label: 'Mietverträge', icon: 'FileText' },
  { id: 'mieteingaenge', label: 'Mieteingänge', icon: 'ArrowDownToLine' },
  { id: 'kosten', label: 'Kosten / Belege', icon: 'Receipt' },
  { id: 'afa', label: 'AfA / Abschreibungen', icon: 'TrendingDown' },
  { id: 'buchhaltung', label: 'Buchhaltung / StB', icon: 'CalendarRange' },
  { id: 'kontoauszuege', label: 'Bank', icon: 'Receipt' },
  { id: 'dokumente', label: 'Dokumente', icon: 'FileText' },
  { id: 'liquiditaet', label: 'Liquidität', icon: 'Wallet' },
  { id: 'datev', label: 'DATEV', icon: 'Download' },
];

/** Privat — schlanker Satz für das private Konto (Sparkasse). */
const FINANZ_SEKTIONEN_PRIVAT: FinanzSektion[] = [
  { id: 'kontoauszuege', label: 'Bank', icon: 'Receipt' },
  { id: 'dokumente', label: 'Dokumente', icon: 'FileText' },
];

/** Steuer / ESt — Veranlagung & Anlage V. */
const FINANZ_SEKTIONEN_STEUER: FinanzSektion[] = [
  { id: 'veranlagung', label: 'Veranlagung', icon: 'BarChart3' },
  { id: 'anlage_v', label: 'Anlage V', icon: 'Building2' },
  { id: 'verkaeufe', label: 'Verkäufe (§23)', icon: 'TrendingDown' },
  { id: 'fahrtkosten', label: 'Fahrtkosten', icon: 'Car' },
  { id: 'dokumente', label: 'Dokumente', icon: 'FileText' },
];

/** Alle — aggregierte, firmenübergreifende Sicht. */
const FINANZ_SEKTIONEN_ALLE: FinanzSektion[] = [
  { id: 'uebersicht', label: 'Übersicht', icon: 'BarChart3' },
  { id: 'aufgaben', label: 'Aufgaben', icon: 'ListChecks' },
  { id: 'bwa', label: 'BWA', icon: 'BarChart3' },
  { id: 'pruefung', label: 'Prüfung', icon: 'ClipboardCheck' },
  { id: 'ust', label: 'USt-Organschaft', icon: 'Landmark' },
  { id: 'steuerberater', label: 'Steuerberater', icon: 'Scale' },
  { id: 'liquiditaet', label: 'Liquidität', icon: 'Wallet' },
  { id: 'datev', label: 'DATEV-Gesamt', icon: 'Download' },
  { id: 'konten', label: 'Konten-Übersicht', icon: 'Banknote' },
];

/** Mapping: Firma-ID → Sektionen des Firma-Untermenüs. */
export const FINANZEN_FIRMA_NAV: Record<FinanzenFirmaId, FinanzSektion[]> = {
  sv_buero: FINANZ_SEKTIONEN_BETRIEB,
  lcs: FINANZ_SEKTIONEN_BETRIEB,
  privat: FINANZ_SEKTIONEN_PRIVAT,
  immobilien: FINANZ_SEKTIONEN_IMMOBILIEN,
  steuer: FINANZ_SEKTIONEN_STEUER,
  alle: FINANZ_SEKTIONEN_ALLE,
};

/** Hilfsfunktion: Firma-Definition nach ID (Fallback: SV Büro). */
export function getFinanzenFirma(id: string): FinanzenFirma {
  return FINANZEN_FIRMEN.find(f => f.id === id) || FINANZEN_FIRMEN[0];
}

/** Hilfsfunktion: Sektionen für eine Firma (Fallback: SV Büro). */
export function getFinanzenSektionen(firmaId: string): FinanzSektion[] {
  return FINANZEN_FIRMA_NAV[firmaId as FinanzenFirmaId] || FINANZEN_FIRMA_NAV.sv_buero;
}

export const SV_VERFAHREN_AUFTRAGSTYPEN = [
  'sachverstaendigenverfahren',
  'sachverständigenverfahren',
  'sv_verfahren',
  'sv-verfahren',
  'svverfahren',
  'schiedsgutachten',
];

export function istSvVerfahren(auftragstyp?: string | null): boolean {
  if (!auftragstyp) return false;
  return SV_VERFAHREN_AUFTRAGSTYPEN.includes(String(auftragstyp).trim().toLowerCase());
}

export function getProzessNav(betriebId: string, auftragstyp?: string | null): AktenProzessItem[] {
  if (betriebId === 'gutachten' && istSvVerfahren(auftragstyp)) {
    return PROZESS_GUTACHTEN_SV;
  }
  return BETRIEB_PROZESS_NAV[betriebId as BetriebId] || PROZESS_GUTACHTEN;
}

export function getSectionLabels(betriebId: string, auftragstyp?: string | null): Record<string, string> {
  const steps = getProzessNav(betriebId, auftragstyp);
  const labels: Record<string, string> = {};
  for (const step of steps) {
    const sectionId = step.path.replace(/^\//, '');
    labels[sectionId] = step.label;
  }
  return labels;
}

export const AKTEN_PROZESS_NAV = PROZESS_GUTACHTEN;

export const EINSTELLUNGEN_NAV = [
  { id: 'allgemein', label: 'Allgemein', icon: 'Settings', status: 'in_entwicklung' as FeatureStatus },
  { id: 'benutzer', label: 'Benutzer & Rechte', icon: 'UserCog', status: 'in_entwicklung' as FeatureStatus },
  { id: 'betriebe', label: 'Betriebe', icon: 'Building2', status: 'in_entwicklung' as FeatureStatus },
  { id: 'benachrichtigungen', label: 'Benachrichtigungen', icon: 'Bell', status: 'geplant' as FeatureStatus },
  { id: 'integrationen', label: 'Integrationen', icon: 'Plug', status: 'geplant' as FeatureStatus },
  { id: 'vorlagen', label: 'Vorlagen', icon: 'FileTemplate', status: 'geplant' as FeatureStatus },
  { id: 'roadmap', label: 'Roadmap & Changelog', icon: 'Map', status: 'aktiv' as FeatureStatus },
];

export interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  category: string;
  priority: 'hoch' | 'mittel' | 'niedrig';
}

export const ROADMAP_ITEMS: RoadmapItem[] = [
  { id: 'r1', title: 'Dashboard-Widgets', description: 'KPI-Karten, Kalender, Aufgaben, Akten-Übersicht', status: 'aktiv', category: 'Dashboard', priority: 'hoch' },
  { id: 'r2', title: 'Akten-System', description: 'CRUD, Betrieb-Filter, Prozessmenü pro Betrieb', status: 'aktiv', category: 'Akten', priority: 'hoch' },
  { id: 'r3', title: 'Google Kalender-Sync', description: 'Bidirektionale Synchronisation mit Google Calendar', status: 'aktiv', category: 'Kalender', priority: 'hoch' },
  { id: 'r4', title: 'Gmail-Integration', description: 'E-Mail senden/empfangen direkt im Dashboard', status: 'aktiv', category: 'Kommunikation', priority: 'hoch' },
  { id: 'r5', title: 'Kunden-Kiosk', description: 'Fullscreen Touch-Interface für Kunden', status: 'aktiv', category: 'Kiosk', priority: 'hoch' },
  { id: 'r6', title: 'Werkstatt-Kiosk', description: 'Werkstatt-Mitarbeiter Interface', status: 'aktiv', category: 'Kiosk', priority: 'mittel' },
  { id: 'r7', title: 'Finanzen-Dashboard', description: 'Rechnungen, DATEV, Buchhaltung', status: 'aktiv', category: 'Finanzen', priority: 'mittel' },
  { id: 'r8', title: 'DAT-API Integration', description: 'Fahrzeugbewertung & Reparaturkalkulation', status: 'geplant', category: 'Integrationen', priority: 'hoch' },
  { id: 'r9', title: 'Restwertbörsen', description: 'CARTV, WOM Anbindung', status: 'geplant', category: 'Integrationen', priority: 'mittel' },
  { id: 'r10', title: 'Mobile App', description: 'React Native App für iOS/Android', status: 'geplant', category: 'Mobile', priority: 'niedrig' },
];

export function getFilteredSidebarNav(
  role: UserRole | undefined,
  permissions: Record<string, number | boolean> | undefined,
  modulePermissions?: Record<string, boolean> | null
): NavItem[] {
  if (!role || !permissions) return SIDEBAR_NAV;
  if (role === 'admin') return SIDEBAR_NAV;
  const ALWAYS_VISIBLE = ['kommunikation', 'aufgaben', 'kalender', 'einstellungen'];
  return SIDEBAR_NAV.filter((item) => {
    if (item.id === 'dashboard') {
      if (modulePermissions && modulePermissions.dashboard === false) return false;
      return true;
    }
    if (ALWAYS_VISIBLE.includes(item.id)) return true;
    if (modulePermissions) return modulePermissions[item.id] === true;
    if (item.requiredRoles && !item.requiredRoles.includes(role)) return false;
    if (item.requiredPermission) {
      const userLevel = (permissions[item.requiredPermission.module] as number) ?? 0;
      if (userLevel < item.requiredPermission.level) return false;
    }
    return true;
  });
}
